// Centralised colour tokens for the renderer.

import type { ExplosionPhase } from '../core/state/types';

export const PALETTE = {
  void: '#05060a',
  sky: '#0a0a1f',
  ground: '#3a1d6e',
  groundLine: '#7b4fd0',
  city: '#38e1ff',
  cityDead: '#2a2350',
  base: '#ffd23f',
  baseDead: '#4a3a16',
  ammo: '#1a237e',
  enemy: '#ff5a5f',
  enemyTrail: 'rgba(255,90,95,0.45)',
  player: '#7cfcec',
  playerTrail: 'rgba(124,252,236,0.5)',
  target: '#7cfcec',
  crosshair: '#ff5a5f',
  hud: '#2fe6c8',
  hudDim: 'rgba(47,230,200,0.6)',
} as const;

/** Explosion fill colour with a high-frequency strobe alternating bright and
 *  dark tones, mimicking the original arcade CRT "flicker". */
export function explosionColor(age: number, phase: ExplosionPhase): string {
  const flash = Math.floor(age * 30) % 2 === 0;
  switch (phase) {
    case 'expanding':
      return flash ? '#ffffff' : '#fff0a0';
    case 'holding':
      return flash ? '#ffe66d' : '#ff9a5c';
    case 'contracting':
      return flash ? '#ff7a3c' : '#a03010';
  }
}

/** Colours that cycle per wave (terrain/sky/enemy), à la the arcade level cycle. */
export interface WavePalette {
  readonly sky: string;
  readonly ground: string;
  readonly groundLine: string;
  readonly enemy: string;
  readonly enemyTrail: string;
}

const WAVE_CYCLE: readonly WavePalette[] = [
  { sky: '#0a0a1f', ground: '#3a1d6e', groundLine: '#7b4fd0', enemy: '#ff5a5f', enemyTrail: 'rgba(255,90,95,0.45)' },
  { sky: '#0c1410', ground: '#13402a', groundLine: '#33b06a', enemy: '#ffd23f', enemyTrail: 'rgba(255,210,63,0.4)' },
  { sky: '#150a18', ground: '#4a1d3a', groundLine: '#d04f9b', enemy: '#5ad1ff', enemyTrail: 'rgba(90,209,255,0.4)' },
  { sky: '#0a1018', ground: '#143a4a', groundLine: '#3f9bd0', enemy: '#ff8a3c', enemyTrail: 'rgba(255,138,60,0.4)' },
];

export function paletteForWave(wave: number): WavePalette {
  const idx = ((wave - 1) % WAVE_CYCLE.length + WAVE_CYCLE.length) % WAVE_CYCLE.length;
  return WAVE_CYCLE[idx]!;
}
